import React, { useState } from 'react';
import { adminAPI, WithdrawalTransferDetails, WithdrawalTransferPreview, WithdrawalTransferRequest } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

const storageKey = 'admin-withdrawal-transfer-pending';
type Pending = { id: string; request: WithdrawalTransferRequest };
const stored = (): Pending | null => {
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { return null; }
};
const money = (n: number | null | undefined) => n == null ? '—' : n.toLocaleString('ru-RU', { maximumFractionDigits: 6 });
const labels: Record<string, string> = { exchange_rate: 'Курс', trader_commission_rate: 'Комиссия, %',
    trader_commission_amount: 'Комиссия, USDT', platform_profit_usdt: 'Прибыль платформы, USDT' };

export const WithdrawalTransferPanel: React.FC<{ onCompleted: () => void }> = ({ onCompleted }) => {
    const [id, setId] = useState('');
    const [details, setDetails] = useState<WithdrawalTransferDetails | null>(null);
    const [target, setTarget] = useState('');
    const [reason, setReason] = useState('');
    const [preview, setPreview] = useState<WithdrawalTransferPreview | null>(null);
    const [unpaid, setUnpaid] = useState(false);
    const [loss, setLoss] = useState(false);
    const [pending, setPending] = useState<Pending | null>(stored);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const run = async (action: () => Promise<void>) => {
        if (busy) return;
        setBusy(true); setError(''); setNotice('');
        try { await action(); } catch (e: any) {
            setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Не удалось получить ответ. Проверьте результат операции.');
        } finally { setBusy(false); }
    };
    const clearPending = () => { localStorage.removeItem(storageKey); setPending(null); };
    const complete = async (transactionId: string) => {
        clearPending(); setPreview(null); setUnpaid(false); setLoss(false); setTarget('');
        setNotice('Выплата перенесена'); onCompleted();
        setId(transactionId); setDetails(await adminAPI.getWithdrawalTransfer(transactionId));
    };
    const submit = async (value: Pending) => {
        try {
            await adminAPI.transferWithdrawal(value.id, value.request);
        } catch (e: any) {
            // A definite validation rejection did not transfer the payment. A lost
            // response keeps the identical request available for reconciliation.
            if ([400, 403, 404, 409, 422].includes(e.response?.status)) {
                clearPending(); setPreview(null); setUnpaid(false); setLoss(false);
            }
            throw e;
        }
        await complete(value.id);
    };
    return <Card>
        <CardHeader><CardTitle>Перенос выплат</CardTitle></CardHeader>
        <CardContent className="space-y-4">
            {error && <p role="alert" className="text-red-700 dark:text-red-300">{error}</p>}
            {notice && <p role="status" className="text-green-700 dark:text-green-300">{notice}</p>}
            {pending ? <div className="space-y-3">
                <p>Результат переноса выплаты {pending.id} требует проверки.</p>
                <Button disabled={busy} onClick={() => void run(async () => {
                    const result = await adminAPI.getWithdrawalTransferResult(pending.id, pending.request.operation_id);
                    if (result.status === 'completed') await complete(pending.id);
                    else setNotice('Перенос пока не зафиксирован. Можно повторить тот же запрос.');
                })}>Проверить результат</Button>{' '}
                <Button disabled={busy} onClick={() => void run(() => submit(pending))}>Повторить запрос</Button>
            </div> : <>
                <div className="flex gap-2">
                    <Input aria-label="ID транзакции" placeholder="ID транзакции" value={id} disabled={busy}
                        onChange={e => { setId(e.target.value); setDetails(null); setPreview(null); }} />
                    <Button disabled={busy || !id.trim()} onClick={() => void run(async () => {
                        setDetails(null); setPreview(null); setTarget(''); setUnpaid(false); setLoss(false);
                        setDetails(await adminAPI.getWithdrawalTransfer(id.trim()));
                    })}>Найти</Button>
                </div>
                {details && <>
                    <p>Трейдер: <strong>{details.trader_name || '—'}</strong>. Выплата: {money(details.amount)} {details.currency}.</p>
                    <p>Срок исполнения: {new Date(details.expires_at).toLocaleString('ru-RU')} — сохраняется при переносе.</p>
                    {details.blocked_reason ? <p role="alert">{details.blocked_reason}</p> : <>
                        <label className="block">Новый исполнитель
                            <select aria-label="Новый исполнитель" className="block w-full rounded border p-2 bg-background" value={target} disabled={busy}
                                onChange={e => { setTarget(e.target.value); setPreview(null); setUnpaid(false); setLoss(false); }}>
                                <option value="">Выберите трейдера и ордер</option>
                                {details.candidates.map(c => <option key={c.order_id} value={c.order_id}>{c.username} — ордер {c.order_id}</option>)}
                            </select>
                        </label>
                        {!details.candidates.length && <p>Нет доступных исполнителей для этой выплаты.</p>}
                        {!!details.unavailable?.length && <details className="rounded border p-3">
                            <summary className="cursor-pointer">Почему другие ордера недоступны ({details.unavailable.length})</summary>
                            <ul className="mt-2 space-y-2">{details.unavailable.map(item => <li key={item.order_id}>
                                <strong>{item.username}</strong> — ордер {item.order_id}: {item.reason}
                            </li>)}</ul>
                        </details>}
                        <label className="block">Причина переноса<Input value={reason} maxLength={1000} disabled={busy} onChange={e => setReason(e.target.value)} /></label>
                        <Button disabled={busy || !target} onClick={() => void run(async () => {
                            setPreview(null); setUnpaid(false); setLoss(false);
                            setPreview(await adminAPI.previewWithdrawalTransfer(details.transaction_id, target));
                        })}>Рассчитать перенос</Button>
                        {preview && <div className="space-y-3">
                            <p>Новый исполнитель: <strong>{preview.target_trader_name}</strong></p>
                            <table className="w-full text-left"><thead><tr><th>Условия</th><th>До</th><th>После</th></tr></thead>
                                <tbody>{Object.entries(labels).map(([key, label]) => <tr key={key}><td>{label}</td><td>{money(preview.before[key])}</td><td>{money(preview.after[key])}</td></tr>)}
                                    <tr><td>Начисление после оплаты, USDT</td><td>{money((preview.before.payer_amount || 0) + (preview.before.trader_commission_amount || 0))}</td><td>{money((preview.after.payer_amount || 0) + (preview.after.trader_commission_amount || 0))}</td></tr>
                                </tbody></table>
                            <label className="block"><input type="checkbox" checked={unpaid} disabled={busy} onChange={e => setUnpaid(e.target.checked)} /> Убедился, что исходный трейдер не отправил средства</label>
                            {(preview.after.platform_profit_usdt || 0) < 0 && <label className="block text-red-700 dark:text-red-300"><input type="checkbox" checked={loss} disabled={busy} onChange={e => setLoss(e.target.checked)} /> Подтверждаю убыток {money(-(preview.after.platform_profit_usdt || 0))} USDT</label>}
                            <Button disabled={busy || !reason.trim() || !unpaid || ((preview.after.platform_profit_usdt || 0) < 0 && !loss)} onClick={() => void run(async () => {
                                const value = { id: details.transaction_id, request: { target_order_id: target,
                                    operation_id: crypto.randomUUID(), fingerprint: preview.fingerprint, reason: reason.trim(),
                                    unpaid_confirmed: unpaid, loss_confirmed: loss } };
                                localStorage.setItem(storageKey, JSON.stringify(value)); setPending(value);
                                await submit(value);
                            })}>Перенести выплату</Button>
                        </div>}
                    </>}
                    {details.history.length > 0 && <div><h3 className="font-semibold">История переносов</h3>
                        {details.history.map(item => <p key={item.operation_id} className="py-2 border-b">
                            {new Date(item.occurred_at).toLocaleString('ru-RU')}: {item.source_trader_id} → {item.target_trader_name}. {item.reason} (администратор: {item.admin_username || 'Имя недоступно'})
                        </p>)}
                    </div>}
                </>}
            </>}
        </CardContent>
    </Card>;
};
